/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import Visibility from '@material-ui/icons/Visibility';
import FormHelperText from '@material-ui/core/FormHelperText';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import InputAdornment from '@material-ui/core/InputAdornment';
import InputLabel from '@material-ui/core/InputLabel';
import Typography from '@material-ui/core/Typography';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Logo } from '../components/App/Header/Logo';
import { alertActions } from '../actions/Alerts';
import { login } from './authService';
import { validation } from './service/validations';
import FooterInfo from './FooterInfo';
import { addSubdirectoryToPath } from '../util/getConfig';
import { getUrlParamValue } from '../util/urlTools';
import { LanguageSelector } from '../studyLobby/LanguageSelector';

const loginBackground = addSubdirectoryToPath('/meta/images/alma-background.png');

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(3),
  },
  submit: {
    marginTop: theme.spacing(5),
    backgroundColor: '#ffffff0f',
  },
  item: {
    paddingTop: '.5em',
    '& .MuiOutlinedInput-input': {
      padding: '10px',
      fontSize: '1.3em',
      lineHeight: '1.3em',
      color: '#ffffffe7',
    },
    '& label': {
      fontSize: '.9375rem',
    },
    '& .MuiOutlinedInput-adornedEnd': {
      paddingRight: 0,
    },
  },
  root: {
    fontSize: '.85em',
  },
  whiteItem: {
    color: 'white;',
  },
  all: {
    height: '100%',
    backgroundImage: `url(${loginBackground})`,
    backgroundSize: 'cover'
  },
  i18n: {
    position: 'absolute',
    top: 0,
    right: 4,
  }
}));

export const Login = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const versionPackage = require('../../package.json');
  const [inputs, setInputs] = useState({
    email: '',
    password: '',
    showPassword: false,
  });

  const VersionNumber = styled.p`
    font-size: 0.8em;
    text-align: left;
    margin-top: 1em;
    padding: .5em;
    color: #ffffff0c;
    -webkit-transition: color 75ms linear;
    -ms-transition: color 75ms linear;
    transition: color 75ms linear;
    :hover {
      cursor: default;
      color: #ffffff90;
      -webkit-transition: color 175ms linear;
      -ms-transition: color 175ms linear;
      transition: color 175ms linear;
    }
    @media (max-width: 400px) {
      align-self: flex-start;
      margin-top: .25em;
      padding-left: .75em;
    }
  `;

  const { email, password } = inputs;
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const alert = useSelector((state) => state.alert);
  const { WITH_RECOVERY_PASS, WITH_LOGIN_REGISTRATION, BOOKLET_URL, HAS_PUBLIC_DEMO } = useSelector((state) => state.config);
  const dispatch = useDispatch();

  // handle public demo with automatic login
  useEffect(() => {
    if ((HAS_PUBLIC_DEMO === true)) {
      const publicDemo = getUrlParamValue('publicdemo');
      if (publicDemo === 'active') {
        const demoUser = process.env.PUBLIC_DEMO_USER;
        const demoPassword = process.env.PUBLIC_DEMO_PASSWORD;
        if (demoUser && demoPassword) {
          login(demoUser, demoPassword);
        }
      }
    }
  }, []);

  // reset login status
  useEffect(() => {
    if (submitted && !errors.email && !errors.password) {
      login(email, password);
    }
    setSubmitted(false);
    dispatch(alertActions.clear());
  }, [submitted]);

  function handleChange(e) {
    const { name, value } = e.target;
    dispatch(alertActions.clear());
    setErrors({});
    setInputs({ ...inputs, [name]: value });
  }

  const handleClickShowPassword = () => {
    setInputs({ ...inputs, showPassword: !inputs.showPassword });
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const err = validation({ email, password });
    setErrors(err);
    if (!err.email && !err.password && email && password) {
      setSubmitted(true);
    }
  };

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      justifyContent="space-between"
      alignItems="center"
      className={classes.all}
    >
      <Box className={classes.i18n}>
        <LanguageSelector />
      </Box>
      <VersionNumber>
        version&nbsp;
        {versionPackage.version.replace('-', '.')}
      </VersionNumber>
      <Logo logoWidth="300px" />
      <Container maxWidth="xs">
        <div className={classes.paper}>
          <Typography
            color="secondary"
            align="left"
            component="h1"
            variant="h4"
          >
            {t('auth.login')}
          </Typography>
          <form className={classes.form} onSubmit={handleSubmit}>
            <Grid item xs={12} className={classes.item}>
              <InputLabel>{t('auth.emailinput')}</InputLabel>
              <TextField
                autoComplete="off"
                name="email"
                type="email"
                fullWidth
                variant="outlined"
                required
                error={!!errors.email}
                defaultValue={inputs.email}
                onChange={handleChange}
                autoFocus
                InputProps={{
                  autoComplete: 'off',
                  form: {
                    autoComplete: 'off',
                  },
                }}
              />

              <FormHelperText className={classes.root} error={!!errors.email}>
                {errors.email && errors.email}
              </FormHelperText>
            </Grid>
            {/* PASSWORD */}
            <Grid item xs={12} className={classes.item}>
              <InputLabel>{t('auth.passwordinput')}</InputLabel>
              <TextField
                autoComplete="off"
                name="password"
                type={inputs.showPassword ? 'text' : 'password'}
                fullWidth
                variant="outlined"
                required
                error={!!errors.password}
                defaultValue={inputs.password}
                onChange={handleChange}
                autoFocus
                InputProps={{
                  autoComplete: 'new-password',
                  form: {
                    autoComplete: 'off',
                  },
                  endAdornment: (
                    // eslint-disable-next-line react/jsx-wrap-multilines
                    <InputAdornment position="start">
                      <IconButton
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                      >
                        {inputs.showPassword ? (
                          <Visibility />
                        ) : (
                          <VisibilityOff />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <FormHelperText className={classes.root} error={!!errors.password}>
                {errors.password && errors.password}
              </FormHelperText>
            </Grid>

            <Grid item xs={12}>
              <Button
                className={classes.submit}
                type="submit"
                fullWidth
                variant="outlined"
                size="large"
                onClick={(e) => handleSubmit(e)}
                disabled={!!errors.email || !!errors.password}
              >
                {t('auth.login')}
              </Button>
              <Divider />
              <Grid
                container
                alignContent="center"
              >
                <FormHelperText className={classes.root} error={!!alert.message}>
                  {alert.message}
                </FormHelperText>
              </Grid>
            </Grid>
          </form>

          {WITH_LOGIN_REGISTRATION && (
            <Grid
              container
              justifyContent="space-around"
              spacing={2}
              className={classes.item}
            >
              <Grid item className={classes.item}>
                <Link to="/register">{t('auth.register')}</Link>
              </Grid>
              {WITH_RECOVERY_PASS && (
                <Grid item className={classes.item}>
                  <Link to="/forgot_password">{t('auth.forgotpassword')}</Link>
                </Grid>
              )}
            </Grid>
          )}
          {BOOKLET_URL && (
            <Box mt={8}>
              <Grid item className={classes.whiteItem}>
                <Link style={{ color: 'gray' }} to="/booklet">{t('auth.help')}</Link>
              </Grid>
            </Box>
          )}
        </div>
      </Container>
      <FooterInfo />
    </Grid>

  );
};
